import {useDataStore} from '@/store/data.store.js'
import {useRouting} from '@/composables/use-routing.js'
import {useLocalStorage} from '@/composables/use-local-storage.js'
import {storeToRefs} from 'pinia'
import {computed, watchEffect} from 'vue'

/**
 * Resolve messenger login URL before the organization API is available
 */
export function useLoginUrl() {

  //Composables
  const {queryParams, removeQueryParam} = useRouting()
  const {getFromLocalStorage, setInLocalStorage} = useLocalStorage()

  //Org sourced messenger login URL
  const store = useDataStore()
  const {organization} = storeToRefs(store)
  const orgMessengerLoginUrl = computed(() => organization.value?.messengerLoginUrl)

  //Store in local storage
  watchEffect(() => {
    if (orgMessengerLoginUrl.value) {
      setInLocalStorage('messengerLoginUrl', orgMessengerLoginUrl.value)
    }
  })

  //Resolved messenger login URL
  const messengerLoginUrl = computed(() => {

    //Return from org if present
    if (orgMessengerLoginUrl.value) {
      return orgMessengerLoginUrl.value
    }

    //Return from local storage
    return getFromLocalStorage('messengerLoginUrl', null)
  })

  //Get decoded messenger login URL from query params
  const getLoginUrlFromQuery = () => {

    //Check if anything is present
    const encoded = queryParams.value['messenger_login_url']
    if (!encoded || typeof encoded !== 'string') {
      return null
    }

    //Decode and check protocol
    try {
      const decoded = decodeURIComponent(encoded.replace(/\+/g, ' '))
      const {protocol} = new URL(decoded)
      if (protocol === 'https:') {
        return decoded
      }
    }
    catch {
      return null
    }
  }

  //Captures and persists the messenger login URL from query params
  const captureLoginUrlFromQuery = async () => {

    //Get URL from query params
    const url = getLoginUrlFromQuery()
    if (!url) {
      return
    }

    //Set in local storage and drop the query param from the URL
    setInLocalStorage('messengerLoginUrl', url)
    await removeQueryParam('messenger_login_url')
  }

  //Expose interface
  return {
    messengerLoginUrl,
    captureLoginUrlFromQuery,
  }
}
